import {
  getEmployeeProfile,
  updatePersonalDetails,
  updateOfficialDetails,
  updateFamilyDetails,
  updateEmergencyContacts,
  updateVehicleDetails,
  updateStatutoryDetails
} from "../services/employee.service";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  PencilSquareIcon,
  CheckIcon,
  XMarkIcon,
  DocumentArrowDownIcon,
  IdentificationIcon,
  CameraIcon,
  LockClosedIcon,
  BriefcaseIcon,
  UserIcon,
  BuildingLibraryIcon,
  ComputerDesktopIcon,
  CalendarDaysIcon,
  ChevronRightIcon,
  UsersIcon,
  UserGroupIcon,
  ChartBarIcon,
  ClockIcon,
  GlobeAsiaAustraliaIcon
} from "@heroicons/react/24/outline";

// --- Hierarchy Mock Data (unchanged) ---
const HIERARCHY_DATA = {
  manager: { name: "Sarah Jenkins", id: "SR-992", role: "Delivery Head", project: "Cloud Transformation" },
  teamLeads: [
    {
      name: "Michael Chen",
      id: "TL-441",
      project: "Modernization - Phase 2",
      members: [
        { name: "Johnathan Doe", id: "EMP-2025-099", project: "Modernization - Phase 2", role: "Software Architect" },
        { name: "Sita Sharma", id: "EMP-2025-102", project: "Modernization - Phase 2", role: "Frontend Dev" },
        { name: "David Miller", id: "EMP-2025-105", project: "Modernization - Phase 2", role: "QA Lead" }
      ]
    },
    {
      name: "Priyanka V",
      id: "TL-445",
      project: "Infrastructure Support",
      members: [
        { name: "Rahul Gupta", id: "EMP-2025-201", project: "Infrastructure Support", role: "SRE Engineer" },
        { name: "Kevin Hart", id: "EMP-2025-205", project: "Infrastructure Support", role: "DevOps Eng" }
      ]
    }
  ]
};

// --- Hierarchy View Component (unchanged) ---
const HierarchyView = () => {
  const [expandedTL, setExpandedTL] = useState(null);
  return (
    <div className="p-0 space-y-2 font-roboto">
      <div className="flex flex-col items-center">
        <div className="bg-gradient-to-r from-indigo-600 to-violet-700 p-4 text-white text-center w-full max-w-md transition-all hover:brightness-110 shadow-md">
          <UserIcon className="h-6 w-6 mx-auto mb-1" />
          <h3 className="text-md font-bold font-poppins uppercase tracking-wide">{HIERARCHY_DATA.manager.name}</h3>
          <p className="text-[10px] font-black uppercase tracking-[2px] opacity-80">{HIERARCHY_DATA.manager.role}</p>
        </div>
        <div className="h-4 w-0.5 bg-indigo-200"></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
        {HIERARCHY_DATA.teamLeads.map((tl) => (
          <div key={tl.id} className="bg-white">
            <div
              onClick={() => setExpandedTL(expandedTL === tl.id ? null : tl.id)}
              className={`cursor-pointer transition-all p-4 hover:bg-indigo-50 ${expandedTL === tl.id ? 'bg-indigo-50 border-l-4 border-indigo-600' : ''}`}
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <UserGroupIcon className="h-5 w-5 text-indigo-600" />
                  <div>
                    <h4 className="text-xs font-bold text-gray-900 font-poppins uppercase">{tl.name}</h4>
                    <p className="text-[9px] text-gray-500 font-black">TL | {tl.id}</p>
                  </div>
                </div>
                <ChevronRightIcon className={`h-4 w-4 text-indigo-400 transition-transform ${expandedTL === tl.id ? 'rotate-90' : ''}`} />
              </div>
            </div>
            <AnimatePresence>
              {expandedTL === tl.id && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-gray-50">
                  {tl.members.map(m => (
                    <div key={m.id} className="border-t border-gray-100 p-3 flex items-center justify-between hover:bg-white transition-colors group">
                      <div>
                        <p className="text-[11px] font-bold text-gray-800 uppercase group-hover:text-indigo-600">{m.name}</p>
                        <p className="text-[9px] text-gray-500">{m.role}</p>
                      </div>
                      <div className="text-[8px] font-black text-white bg-indigo-500 px-1.5 py-0.5 uppercase">Staff</div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Tabular Field Component ---
const DataRow = ({ label, value, isEditable = false, editField, onClick }) => {
  return (
    <div
      onClick={onClick}
      className={`flex flex-col p-4 border-b border-gray-100 border-r transition-all duration-200 font-roboto group relative overflow-hidden
        ${onClick ? 'cursor-pointer hover:bg-emerald-50' : 'bg-white hover:bg-emerald-50'}
        ${isEditable ? 'bg-emerald-50/30' : ''}`}
    >
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />
    
      <span className={`text-[9px] font-black uppercase tracking-[0.12em] mb-1.5 flex items-center gap-1.5 ${onClick ? 'text-emerald-600' : 'text-slate-400'}`}>
        {label} {!isEditable && !onClick && <LockClosedIcon className="h-2.5 w-2.5 opacity-40" />}
      </span>
    
      {isEditable ? (
        <div className="flex items-center">
          {editField}
        </div>
      ) : (
        <span className={`text-[13px] font-bold font-poppins tracking-tight truncate ${onClick ? 'text-emerald-700 flex items-center justify-between' : 'text-slate-800'}`}>
          {value || "—"}
          {onClick && <ChevronRightIcon className="h-3.5 w-3.5 ml-2 text-emerald-500 group-hover:translate-x-1 transition-transform" />}
        </span>
      )}
    </div>
  );
};

const MyProfile = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("basic");
  const [isEditing, setIsEditing] = useState(false);
  const [profileImg, setProfileImg] = useState(null);
  
  const initialProfileState = {
    employeeCode: "—",
    firstName: "—",
    middleName: "—",
    surname: "—",
    employmentType: "—",
    reportingManager: "—",
    dob: "",
    gender: "—",
    bloodGroup: "—",
    email: "—",
    personalEmail: "—",
    status: "—",
    doj: "—",
    groupDoj: "—",
    fatherName: "—",
    wifeName: "—",
    wifeDob: "—",
    children: "—",
    pan: "—",
    aadhaar: "—",
    nationality: "—",
    emergencyContact: "—",
    emergencyMobile: "—",
    emergencyRelation: "—",
    vehicleNo: "—",
    dlNo: "—",
    tempAddress: "—",
    permAddress: "—",
    lastWorkingDay: "N/A",
    resignationDate: "N/A",
    terminationReason: "N/A",
    terminationAction: "N/A",
    actualTermDate: "N/A",
    entity: "—",
    dept: "—",
    location: "—",
    grade: "—",
    designation: "—",
    projectCode: "—",
    bankName: "—",
    accountNo: "—",
    ifsc: "—",
    pfNo: "—",
    uan: "—",
    esiNo: "—",
    ppfNo: "—",
    assignedAsset: "—",
    assetSerial: "—",
    assetDate: "—",
    assignedProject: "—"
  };

  const [profile, setProfile] = useState(initialProfileState);
  const [editForm, setEditForm] = useState(initialProfileState);

  // --- Normalization Mapper ---
  const normalizeProfileData = (data) => {
    if (!data) return initialProfileState;

    const emp = data.employee || {};
    const statutory = data.statutory || {};
    const emergency = (data.emergency_contacts && data.emergency_contacts.length > 0) 
      ? data.emergency_contacts[0] 
      : {};

    return {
      employeeCode: emp.emp_code || "—",
      firstName: emp.first_name || "—",
      middleName: emp.middle_name || "—",
      surname: emp.last_name || "—",
      employmentType: emp.employment_type || "—",
      reportingManager: emp.reporting_manager || "—",
      dob: emp.dob || "",
      gender: emp.gender || "—",
      bloodGroup: emp.blood_group || "—",
      email: emp.official_email || "—",
      personalEmail: emp.personal_email || "—",
      status: emp.status || "—",
      doj: emp.date_of_joining || "—",
      groupDoj: emp.group_joining_date || "—",
      fatherName: emp.father_name || "—",
      wifeName: emp.spouse_name || "—",
      wifeDob: emp.spouse_dob || "—",
      children: emp.children || "—",
      pan: statutory.pan_number || "—",
      aadhaar: statutory.aadhar_number || "—",
      nationality: emp.nationality || "—",
      emergencyContact: emergency.contact_name || "—",
      emergencyMobile: emergency.contact_mobile || "—",
      emergencyRelation: emergency.relationship || "—",
      vehicleNo: emp.vehicle_no || "—",
      dlNo: emp.driving_license_no || "—",
      tempAddress: emp.current_address || "—",
      permAddress: emp.permanent_address || "—",
      lastWorkingDay: emp.last_working_day || "N/A",
      resignationDate: emp.resignation_date || "N/A",
      terminationReason: emp.termination_reason || "N/A",
      terminationAction: emp.termination_action || "N/A",
      actualTermDate: emp.actual_term_date || "N/A",
      entity: emp.legal_entity || "—",
      dept: emp.department || "—",
      location: emp.work_location || "—",
      grade: emp.grade || "—",
      designation: emp.designation || "—",
      projectCode: emp.project_code || "—",
      bankName: statutory.bank_name || "—",
      accountNo: statutory.account_number || "—",
      ifsc: statutory.ifsc_code || "—",
      pfNo: statutory.pf_number || "—",
      uan: statutory.uan_number || "—",
      esiNo: statutory.esi_number || "—",
      ppfNo: statutory.ppf_number || "—",
      assignedAsset: emp.assigned_asset || "—",
      assetSerial: emp.asset_serial_no || "—",
      assetDate: emp.asset_issue_date || "—",
      assignedProject: emp.assigned_project || "—"
    };
  };

  const fetchProfileData = async () => {
    const empCode = localStorage.getItem("emp_code");
    if (empCode) {
      try {
        const response = await getEmployeeProfile(empCode);
        if (response) {
          const normalized = normalizeProfileData(response);
          setProfile(normalized);
          setEditForm(normalized);
        }
      } catch (error) {
        // Silently handle
      }
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, []);

  const completionPercentage = useMemo(() => {
    const fieldsToTrack = ['dob', 'bloodGroup', 'personalEmail', 'fatherName', 'wifeName', 'wifeDob', 'children', 'emergencyContact', 'emergencyMobile', 'vehicleNo', 'dlNo', 'tempAddress', 'permAddress'];
    const filledFields = fieldsToTrack.filter(field => profile[field] && profile[field] !== "N/A" && profile[field] !== "" && profile[field] !== "—");
    return Math.round((filledFields.length / fieldsToTrack.length) * 100);
  }, [profile]);

  const tenureYears = 3;
  const tenureMonths = 10;
  const tenureText = `${tenureYears}y ${tenureMonths}m`;

  const handleSave = async () => {
    const empCode = localStorage.getItem("emp_code");
    if (!empCode) return;

    const personalPayload = {
      first_name: editForm.firstName === "—" ? null : editForm.firstName,
      middle_name: editForm.middleName === "—" ? null : editForm.middleName,
      last_name: editForm.surname === "—" ? null : editForm.surname,
      dob: editForm.dob || null,
      gender: editForm.gender === "—" ? null : editForm.gender,
      blood_group: editForm.bloodGroup === "—" ? null : editForm.bloodGroup,
      personal_email: editForm.personalEmail === "—" ? null : editForm.personalEmail,
      father_name: editForm.fatherName === "—" ? null : editForm.fatherName,
      spouse_name: editForm.wifeName === "—" ? null : editForm.wifeName,
      spouse_dob: editForm.wifeDob === "—" ? null : editForm.wifeDob,
      children: editForm.children === "—" ? null : editForm.children,
      nationality: editForm.nationality === "—" ? null : editForm.nationality,
      current_address: editForm.tempAddress === "—" ? null : editForm.tempAddress
    };

    try {
      const response = await updatePersonalDetails(empCode, personalPayload);
      if (response) {
        // Syncing with backend by re-fetching to ensure persistence
        await fetchProfileData();
        setIsEditing(false);
      }
    } catch (error) {
      // Silently handle
    }
  };

  const tabs = [
    { id: "basic", label: "Basic Info", icon: UserIcon },
    { id: "org", label: "Organization Details", icon: BriefcaseIcon },
    { id: "bank", label: "Bank & Statutory", icon: BuildingLibraryIcon },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-indigo-50 p-0 md:p-6 lg:p-10 font-roboto antialiased text-gray-900">
      <div className="max-w-[1500px] mx-auto flex flex-col lg:flex-row bg-white shadow-2xl overflow-hidden border border-slate-200 rounded-lg">
      
        <aside className="lg:w-80 border-r border-slate-200 bg-gradient-to-b from-slate-50 to-slate-100">
          <div className="p-8 flex flex-col items-center space-y-8">
            <div className="relative">
              <div className="w-36 h-36 bg-white border-4 border-indigo-100 shadow-xl flex items-center justify-center overflow-hidden">
                {profileImg ? (
                  <img src={profileImg} alt="User" className="w-full h-full object-cover" />
                ) : (
                  <UserIcon className="w-16 h-16 text-indigo-200" />
                )}
              </div>
              <button
                onClick={() => fileInputRef.current.click()}
                className="absolute -bottom-2 -right-2 bg-indigo-600 p-2.5 text-white hover:bg-emerald-600 transition-all shadow-lg rounded-full"
              >
                <CameraIcon className="h-4 w-4" />
              </button>
              <input type="file" ref={fileInputRef} className="hidden" onChange={(e) => setProfileImg(URL.createObjectURL(e.target.files[0]))} />
            </div>
            <div className="text-center">
              <h2 className="font-bold font-poppins text-slate-900 text-2xl uppercase tracking-tighter leading-none">{profile.firstName} {profile.surname}</h2>
              <p className="text-[11px] font-black text-indigo-600 tracking-[0.25em] mt-3 font-roboto uppercase">{profile.employeeCode}</p>
              <div className="mt-5 px-6 py-1.5 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest font-roboto inline-block">
                {profile.employmentType}
              </div>
            </div>

            <div className="w-full bg-white border border-slate-200 rounded-lg shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-black text-slate-600 uppercase tracking-wider">Profile Completion</span>
                <span className="text-xs font-bold text-emerald-600">{completionPercentage}%</span>
              </div>
              <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden mb-5">
                <motion.div 
                  initial={{ width: 0 }} 
                  animate={{ width: `${completionPercentage}%` }} 
                  className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600"
                />
              </div>

              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-black text-slate-600 uppercase tracking-wider">Employment Tenure</span>
                <span className="text-xs font-bold text-indigo-600">{tenureText}</span>
              </div>
              <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden mb-5">
                <motion.div 
                  initial={{ width: 0 }} 
                  animate={{ width: "78%" }} 
                  className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-600 uppercase">Grade</span>
                  <span className="px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-md">{profile.grade}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-600 uppercase">Years</span>
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-md">{tenureYears}</span>
                </div>
              </div>
            </div>

            <div className="w-full bg-white p-4 border border-slate-200 shadow-sm">
              <div className="flex justify-between items-end mb-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Profile Integrity</span>
                <span className="text-[10px] font-black text-emerald-600">{completionPercentage}%</span>
              </div>
              <div className="w-full bg-slate-100 h-1.5 overflow-hidden rounded-full">
                <motion.div initial={{ width: 0 }} animate={{ width: `${completionPercentage}%` }} className="h-full bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full" />
              </div>
            </div>
            <button
              onClick={() => alert('Exporting...')}
              className="w-full flex items-center justify-center gap-3 bg-white border-2 border-slate-900 text-slate-900 py-3 text-[11px] font-black uppercase tracking-widest hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all font-roboto shadow-md"
            >
              <IdentificationIcon className="h-5 w-5" /> Download ID Card
            </button>
          </div>
        </aside>

        <main className="flex-1 flex flex-col bg-white">
          <div className="flex bg-slate-50 border-b border-slate-200 overflow-x-auto scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2.5 px-6 py-3.5 text-[10.5px] font-black uppercase tracking-[0.15em] transition-all border-r border-slate-200 font-roboto whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-white text-emerald-700 border-t-3 border-t-emerald-600 shadow-sm"
                    : "text-slate-500 hover:bg-white hover:text-emerald-600"
                }`}
              >
                <tab.icon className={`h-4 w-4 ${activeTab === tab.id ? "text-emerald-600" : "text-slate-400"}`} /> {tab.label}
              </button>
            ))}
          </div>
          <div className="px-6 py-4 flex justify-between items-center bg-white border-b border-slate-100">
            <button
              onClick={() => navigate(-1)}
              className="text-slate-500 hover:text-emerald-700 flex items-center gap-2 text-[10px] font-black tracking-widest transition-all font-roboto group"
            >
              <ArrowLeftIcon className="h-3 w-3 group-hover:-translate-x-1 transition-transform" /> Back to Dashboard
            </button>
            {activeTab === "basic" && (
              !isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 bg-emerald-600 text-white px-5 py-2 text-[10.5px] font-black uppercase tracking-widest hover:bg-emerald-700 transition-all font-roboto shadow-md"
                >
                  <PencilSquareIcon className="h-3.5 w-3.5" /> Edit Profile
                </button>
              ) : (
                <div className="flex gap-3">
                  <button
                    onClick={handleSave}
                    className="bg-emerald-600 text-white px-5 py-2 text-[10.5px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-emerald-700 transition-all font-roboto shadow-md"
                  >
                    <CheckIcon className="h-3.5 w-3.5" /> Save
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditForm(profile);
                    }}
                    className="bg-slate-200 text-slate-700 px-5 py-2 text-[10.5px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-slate-300 transition-all font-roboto"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" /> Cancel
                  </button>
                </div>
              )
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            <AnimatePresence mode="wait">
              {activeTab === "basic" && (
                <motion.div key="basic" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-slate-100">
                  <DataRow label="Employee Code" value={profile.employeeCode} />
                  <DataRow label="First Name" value={profile.firstName} isEditable={isEditing} editField={<input className="text-[13px] p-0 outline-none border-b-2 border-emerald-500 bg-transparent w-full font-bold text-emerald-700" value={editForm.firstName === "—" ? "" : editForm.firstName} onChange={e => setEditForm({ ...editForm, firstName: e.target.value })} />} />
                  <DataRow label="Middle Name" value={profile.middleName} isEditable={isEditing} editField={<input className="text-[13px] p-0 outline-none border-b-2 border-emerald-500 bg-transparent w-full font-bold text-emerald-700" value={editForm.middleName === "—" ? "" : editForm.middleName} onChange={e => setEditForm({ ...editForm, middleName: e.target.value })} />} />
                  <DataRow label="Surname" value={profile.surname} isEditable={isEditing} editField={<input className="text-[13px] p-0 outline-none border-b-2 border-emerald-500 bg-transparent w-full font-bold text-emerald-700" value={editForm.surname === "—" ? "" : editForm.surname} onChange={e => setEditForm({ ...editForm, surname: e.target.value })} />} />
                  <DataRow label="Contract Type" value={profile.employmentType} />
                  <DataRow label="Joining Date" value={profile.doj} />
                  <DataRow label="Birth Date" value={profile.dob} isEditable={isEditing} editField={<input type="date" className="text-[13px] p-0 outline-none border-b-2 border-emerald-500 bg-transparent font-bold text-emerald-700" value={editForm.dob} onChange={e => setEditForm({ ...editForm, dob: e.target.value })} />} />
                  <DataRow label="Gender" value={profile.gender} />
                  <DataRow label="Blood Group" value={profile.bloodGroup} isEditable={isEditing} editField={<input className="text-[13px] p-0 outline-none border-b-2 border-emerald-500 bg-transparent font-bold text-emerald-700" value={editForm.bloodGroup === "—" ? "" : editForm.bloodGroup} onChange={e => setEditForm({ ...editForm, bloodGroup: e.target.value })} />} />
                  <DataRow label="Corporate Email" value={profile.email} />
                  <DataRow label="Personal Email" value={profile.personalEmail} isEditable={isEditing} editField={<input className="text-[13px] p-0 outline-none border-b-2 border-emerald-500 bg-transparent w-full font-bold text-emerald-700" value={editForm.personalEmail === "—" ? "" : editForm.personalEmail} onChange={e => setEditForm({ ...editForm, personalEmail: e.target.value })} />} />
                  <DataRow label="Work Status" value={profile.status} />
                  <DataRow label="Father's Name" value={profile.fatherName} isEditable={isEditing} editField={<input className="text-[13px] p-0 outline-none border-b-2 border-emerald-500 bg-transparent font-bold text-emerald-700" value={editForm.fatherName === "—" ? "" : editForm.fatherName} onChange={e => setEditForm({ ...editForm, fatherName: e.target.value })} />} />
                  <DataRow label="Spouse Name" value={profile.wifeName} isEditable={isEditing} editField={<input className="text-[13px] p-0 outline-none border-b-2 border-emerald-500 bg-transparent font-bold text-emerald-700" value={editForm.wifeName === "—" ? "" : editForm.wifeName} onChange={e => setEditForm({ ...editForm, wifeName: e.target.value })} />} />
                  <DataRow label="Emergency Contact" value={profile.emergencyContact} isEditable={isEditing} editField={<input className="text-[13px] p-0 outline-none border-b-2 border-emerald-500 bg-transparent font-bold text-emerald-700" value={editForm.emergencyContact === "—" ? "" : editForm.emergencyContact} onChange={e => setEditForm({ ...editForm, emergencyContact: e.target.value })} />} />
                  <DataRow label="Emergency Mobile" value={profile.emergencyMobile} isEditable={isEditing} editField={<input className="text-[13px] p-0 outline-none border-b-2 border-emerald-500 bg-transparent font-bold text-emerald-700" value={editForm.emergencyMobile === "—" ? "" : editForm.emergencyMobile} onChange={e => setEditForm({ ...editForm, emergencyMobile: e.target.value })} />} />
                  <DataRow label="PAN Number" value={profile.pan} />
                  <DataRow label="Aadhaar ID" value={profile.aadhaar} />
                  <div className="md:col-span-2 lg:col-span-4">
                    <DataRow label="Current Residential Address" value={profile.tempAddress} isEditable={isEditing} editField={<textarea rows="2" className="text-[13px] p-0 outline-none border-b-2 border-emerald-500 bg-transparent w-full font-bold text-emerald-700 resize-none" value={editForm.tempAddress === "—" ? "" : editForm.tempAddress} onChange={e => setEditForm({ ...editForm, tempAddress: e.target.value })} />} />
                  </div>
                </motion.div>
              )}
              {activeTab === "org" && (
                <motion.div key="org" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="grid grid-cols-1 md:grid-cols-3 border-l border-slate-100">
                  <DataRow label="Legal Entity" value={profile.entity} />
                  <DataRow label="Department" value={profile.dept} />
                  <DataRow label="Reporting Manager" value={profile.reportingManager} onClick={() => navigate("/dashboard/org-structure")} />
                  <DataRow label="Site Location" value={profile.location} />
                  <DataRow label="Grade / Level" value={profile.grade} />
                  <DataRow label="Designation" value={profile.designation} />
                  <DataRow label="Project Code" value={profile.projectCode} />
                  <DataRow label="Last Working Day" value={profile.lastWorkingDay} />
                </motion.div>
              )}
              {activeTab === "bank" && (
                <motion.div key="bank" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <div className="grid grid-cols-1 md:grid-cols-3 border-l border-slate-100">
                    <DataRow label="Bank Name" value={profile.bankName} />
                    <DataRow label="Account Number" value={profile.accountNo} />
                    <DataRow label="IFSC Code" value={profile.ifsc} />
                    <DataRow label="Provident Fund" value={profile.pfNo} />
                    <DataRow label="UAN" value={profile.uan} />
                  </div>
                  <div className="bg-slate-900 p-4 flex items-center gap-3">
                    <ComputerDesktopIcon className="h-5 w-5 text-emerald-400" />
                    <h4 className="text-[11px] font-black text-white uppercase tracking-[0.2em] font-roboto">IT Assets & Infrastructure</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 border-l border-slate-100">
                    <div className="md:col-span-3"><DataRow label="Assigned Project" value={profile.assignedProject} /></div>
                    <DataRow label="Asset Identity" value={profile.assignedAsset} />
                    <DataRow label="Serial Number" value={profile.assetSerial} />
                    <DataRow label="Issue Date" value={profile.assetDate} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MyProfile;